import { Container, H2, H4 } from "@carrotfertility/carotene"
import { useCurrentUser } from "components/context/user/UserContext"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
} from "components/csd-enrollment/shared"
import { Steps } from "components/csd-enrollment/workflow/steps"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import L10n from "lib/carrot-l10n"
import React, { FormEventHandler, useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import {
  FieldNameHealthPlanId,
  FieldNameTradingPartner,
  UseConnectHealthPlan,
  useConnectHealthPlan,
  UseTradingPartners,
  useTradingPartners,
  UseTradingPartnersArgs
} from "./hooks"
import { UpdateHealthPlan } from "./types"
import { Form } from "../../../../views/atoms/forms/Form"

export interface ProviderConnectYourInsuranceProps
  extends UseConnectHealthPlan,
    Pick<UseTradingPartners, "options" | "providerListed" | "checkIfProviderIsListed">,
    Pick<UseTradingPartnersArgs, "defaultTradingPartnerPk"> {
  stepHeader: string
  defaultHealthPlanId: string
}

export interface ProviderConnectYourInsuranceViewProps
  extends UseTradingPartnersArgs,
    Pick<ProviderConnectYourInsuranceProps, "stepHeader"> {
  viewName: typeof Steps[keyof typeof Steps]
  onUserUpdate: (healthPlanIdVal: string, tradingPartnerPkVal: string, tradingPartnerId: string) => any
  defaultHealthPlanId
}
const l = new L10n({ locale: "en-US" }).getBoundLocalize()
function ProviderConnectYourInsurance({
  defaultTradingPartnerPk,
  options,
  providerListed,
  checkIfProviderIsListed,
  stepHeader,
  onSubmit,
  defaultHealthPlanId
}: ProviderConnectYourInsuranceProps): JSX.Element {
  const formMethods = useForm()
  const { register, handleSubmit, errors, setValue } = formMethods

  useEffect(() => {
    setValue(FieldNameHealthPlanId, defaultHealthPlanId)
  }, [])

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Container padding="none" stack="huge">
          <BenefitEnrollmentFlowModalBackButton />
        </Container>
        <H2 stack="large" variant="primary">
          {stepHeader}
        </H2>
        <H4 stack="tiny" variant="primary">{l`PROVIDER NAME`}</H4>
        <BenefitEnrollmentModalFormSelect
          name={FieldNameTradingPartner}
          defaultValue={defaultTradingPartnerPk}
          options={options}
          getSelectedValue={checkIfProviderIsListed}
          onChange={(checkIfProviderIsListed as unknown) as FormEventHandler<HTMLLabelElement>}
        />
        <Container padding="none" stack={providerListed ? "large" : "giant"}>
          {providerListed && (
            <>
              <H4 stack="tiny" variant="primary">{l`MEMBER ID`}</H4>
              <CompleteFormInput
                name={FieldNameHealthPlanId}
                errors={errors}
                ref={register({
                  required: "Required",
                  pattern: { value: /^[a-zA-Z0-9]+$/, message: "Only numbers and letters may be entered." }
                })}
              />
            </>
          )}
        </Container>
        <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
      </Form>
    </FormProvider>
  )
}

function ProviderConnectYourInsuranceView({
  viewName,
  defaultTradingPartnerPk,
  defaultHealthPlanId,
  onUserUpdate,
  stepHeader
}: ProviderConnectYourInsuranceViewProps): JSX.Element {
  const { options, providerListed, availableTradingPartners, checkIfProviderIsListed } = useTradingPartners({
    defaultTradingPartnerPk
  })
  const { onSubmit } = useConnectHealthPlan({ viewName, onUserUpdate, availableTradingPartners })

  return (
    <ProviderConnectYourInsurance
      {...{
        defaultTradingPartnerPk,
        options,
        providerListed,
        checkIfProviderIsListed,
        stepHeader,
        onSubmit,
        defaultHealthPlanId
      }}
    />
  )
}

export function MemberConnectHealthPlanStep(): JSX.Element {
  const { selectedTradingPartnerPk: defaultTradingPartnerPk, memberId: defaultHealthPlanId } = useCurrentUser()
  const { updateUserInfo } = useCurrentUser()
  function onUserUpdate(
    healthPlanIdVal: string,
    tradingPartnerPkVal: string,
    tradingPartnerId: string
  ): UpdateHealthPlan {
    return updateUserInfo({
      selectedTradingPartnerId: tradingPartnerId,
      memberId: healthPlanIdVal,
      selectedTradingPartnerPk: tradingPartnerPkVal
    }) as any
  }

  return (
    <ProviderConnectYourInsuranceView
      {...{ defaultTradingPartnerPk, defaultHealthPlanId, onUserUpdate }}
      stepHeader={l`Connect to your health insurance provider`}
      viewName={Steps.MEMBER_HEALTH_PLAN_CONNECT}
    />
  )
}

export function PartnerConnectHealthPlanStep(): JSX.Element {
  const {
    partnerHealthPlanSelectedTradingPartnerPk: defaultTradingPartnerPk,
    partnerHealthPlanMemberId: defaultHealthPlanId
  } = useCurrentUser()
  const { updateUserInfo } = useCurrentUser()

  function onUserUpdate(
    healthPlanIdVal: string,
    tradingPartnerPkVal: string,
    tradingPartnerId: string
  ): UpdateHealthPlan {
    return updateUserInfo({
      partnerHealthPlanSelectedTradingPartnerId: tradingPartnerId,
      partnerHealthPlanMemberId: healthPlanIdVal,
      partnerHealthPlanSelectedTradingPartnerPk: tradingPartnerPkVal
    }) as any
  }
  return (
    <ProviderConnectYourInsuranceView
      {...{ defaultTradingPartnerPk, defaultHealthPlanId, onUserUpdate }}
      stepHeader={l`Connect to their health insurance provider`}
      viewName={Steps.PARTNER_HEALTH_PLAN_CONNECT}
    />
  )
}
